import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    "Missing Supabase env vars. Define VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in Supabase/.env or .env.local",
  );
}

// ---------------------------------------------------------------------------
// Recuperación de sesión ante 401 (caso Kilimanjaro, 2026-10-19).
//
// supabase-js decide cuándo renovar el access token mirando el reloj de la PC.
// Si la PC tiene la hora atrasada (en Kilimanjaro, 1 h), cree que el token
// sigue vigente, nunca lo renueva y el servidor rechaza todo con 401 "JWT
// expired": la UI sigue "logueada" pero no graba ventas.
//
// Este fetch envuelve las llamadas de datos (REST y Storage, y Edge Functions
// solo cuando el rechazo es del gateway por JWT): ante un 401 por token
// vencido/inválido fuerza un refreshSession() — que no depende del reloj
// local — y reintenta UNA vez con el token nuevo. Reintentar es seguro: un
// 401 de JWT se corta antes de ejecutar la consulta.
//
// Las llamadas a /auth/v1 no pasan por acá (evita loops con el propio refresh).
// ---------------------------------------------------------------------------

const JWT_ERROR_PATTERN = /jwt|token/i;
const POSTGREST_JWT_CODES = new Set(["PGRST301", "PGRST302", "PGRST303"]);

let supabaseRef = null;
let refreshInFlight = null;

const getRequestUrl = (input) =>
  typeof input === "string" ? input : input?.url || String(input || "");

const getRequestKind = (url) => {
  if (url.includes("/rest/v1/")) return "rest";
  if (url.includes("/storage/v1/")) return "storage";
  if (url.includes("/functions/v1/")) return "functions";
  return null;
};

const isJwtRejection = async (response, kind) => {
  try {
    const text = await response.clone().text();
    let body = null;
    try { body = JSON.parse(text); } catch { /* cuerpo no JSON */ }
    const code = String(body?.code ?? "");
    const message = String(body?.message ?? body?.msg ?? body?.error ?? text ?? "");

    if (kind === "rest") return POSTGREST_JWT_CODES.has(code) || JWT_ERROR_PATTERN.test(message);
    // Edge Functions: solo el rechazo del gateway ("Invalid JWT"). Un 401
    // devuelto por la propia función puede venir después de efectos (ARCA,
    // pasarelas) y NO se reintenta.
    if (kind === "functions") return /invalid jwt|jwt expired/i.test(message);
    return JWT_ERROR_PATTERN.test(message);
  } catch {
    return false;
  }
};

// Un solo refresh en vuelo por pestaña: si diez requests reciben 401 a la vez,
// comparten la misma renovación (el refresh token de Supabase es de un uso).
const refreshAccessToken = () => {
  if (!supabaseRef) return Promise.resolve(null);
  if (!refreshInFlight) {
    refreshInFlight = supabaseRef.auth
      .refreshSession()
      .then(({ data, error }) => (error ? null : data?.session?.access_token || null))
      .catch(() => null)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
};

const fetchWithSessionRecovery = async (input, init = {}) => {
  const response = await fetch(input, init);
  if (response.status !== 401) return response;

  const kind = getRequestKind(getRequestUrl(input));
  if (!kind) return response;

  const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
  const authorization = headers.get("Authorization") || "";
  // Request anónimo (sin usuario logueado): no hay sesión que renovar.
  if (!authorization || authorization === `Bearer ${supabaseAnonKey}`) return response;

  if (!(await isJwtRejection(response, kind))) return response;

  const freshToken = await refreshAccessToken();
  if (!freshToken) return response; // refresh token muerto: el caller ve el 401 original.

  headers.set("Authorization", `Bearer ${freshToken}`);
  return fetch(input, { ...init, headers });
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
  global: {
    fetch: fetchWithSessionRecovery,
  },
});

supabaseRef = supabase;

// Error de sesión vencida que llega a la UI (el reintento ya falló: el refresh
// token fue revocado o la sesión se cerró en otro lado).
export const isSessionExpiredError = (error) => {
  if (!error) return false;
  const code = String(error.code ?? "");
  const message = String(error.message ?? "");
  return POSTGREST_JWT_CODES.has(code) || /jwt expired|invalid jwt|jwt.*(expired|invalid)/i.test(message);
};

export default supabase;
